/**
 * Drop-in replacement for `formik` that trims all string values on submit
 * and on blur for text-like Field components.
 * Import from "lib/formik" instead of "formik".
 */
import React from "react"
import {
  Formik as FormikBase,
  useFormik as useFormikBase,
  Form,
  Field as FieldBase,
  ErrorMessage,
  useField,
  useFormikContext,
  FieldArray,
  FormikProvider,
  connect,
  getIn,
  yupToFormErrors,
  FastField,
} from "formik"
import { withTrimmedSubmit, trimString } from "utils/formUtils"

const TRIM_ON_BLUR_TYPES = new Set([
  "text",
  "email",
  "search",
  "tel",
  "url",
  "password",
  "textarea",
  undefined,
])

export function Formik({ onSubmit, ...props }) {
  return <FormikBase onSubmit={withTrimmedSubmit(onSubmit)} {...props} />
}

export function useFormik({ onSubmit, ...config } = {}) {
  return useFormikBase({
    ...config,
    onSubmit: withTrimmedSubmit(onSubmit),
  })
}

export function Field({ name, onBlur, type, component, as, ...props }) {
  const formik = useFormikContext()
  const resolvedType =
    type ||
    (typeof component === "string" ? component : undefined) ||
    (typeof as === "string" ? as : undefined)

  const handleBlur = e => {
    if (onBlur) {
      onBlur(e)
    }
    if (formik?.handleBlur) {
      formik.handleBlur(e)
    }
    if (
      formik?.setFieldValue &&
      name &&
      TRIM_ON_BLUR_TYPES.has(resolvedType)
    ) {
      const raw = e?.target?.value ?? ""
      formik.setFieldValue(name, trimString(raw))
    }
  }

  return (
    <FieldBase
      name={name}
      type={type}
      component={component}
      as={as}
      onBlur={handleBlur}
      {...props}
    />
  )
}

export {
  Form,
  ErrorMessage,
  useField,
  useFormikContext,
  FieldArray,
  FormikProvider,
  connect,
  getIn,
  yupToFormErrors,
  FastField,
}
